/**
 * Finding a reagent by typing its name, from the title bar.
 *
 * The only one. The shelf used to carry a field of its own, between the two
 * rows, which picked a bottle out where it stood; this reaches a reagent you
 * already know the name of without reading a shelf at all. The two answered
 * different questions, and the difference was not worth two fields: a student
 * mid-experiment asking "where is the silver nitrate" wants to be handed it,
 * and the shelf's field cost both rows width to say "it is over there".
 *
 * It searches the name AND the formula, because half of what a student knows a
 * reagent by is AgNO3.
 */
import { useEffect, useRef, useState } from 'react';
import { SUBSTANCE_CATALOG, type Substance } from '../substanceCatalog';
import { ChemFormula } from './ChemFormula';
import { SubstanceIcon } from './SubstanceIcon';

/** Enough to choose from without becoming the shelf again. */
const MAX_HITS = 7;

function matches(query: string): Substance[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return SUBSTANCE_CATALOG
    // A gas is never dispensed from the shelf — it arrives through a tube —
    // so it is not something this can hand you. See Substance.dose.
    .filter(s => s.dose && (
      s.displayName.toLowerCase().includes(q) || s.formula.toLowerCase().replace(/[()]/g, '').includes(q)
    ))
    // A name that STARTS with what was typed is what was meant: "sodium" must
    // not offer Ammonium Sulfate first for containing the letters somewhere.
    .sort((a, b) => Number(b.displayName.toLowerCase().startsWith(q)) - Number(a.displayName.toLowerCase().startsWith(q)))
    .slice(0, MAX_HITS);
}

export function ReagentSearch({ onAdd, disabled }: {
  /** `at` is where on screen the reagent was taken from, so anything that asks
   *  a question before the pour — the quantitative bench's strength chooser —
   *  opens there. Without it the pour happened silently, at the catalogue's
   *  strength, from the one search the bench has. */
  onAdd: (substanceId: string, at?: { x: number; y: number }) => void;
  disabled?: boolean;
}): React.JSX.Element {
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const hits = matches(query);

  // Clicking anywhere else puts it away. Escape does too, from the field.
  useEffect(() => {
    if (!query) return;
    const away = (e: PointerEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setQuery('');
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [query]);

  /** Where a choice made here should be asked about: under the field, since
   *  that is where the eye already is — for a click and for Enter alike. */
  const below = (): { x: number; y: number } | undefined => {
    const box = ref.current?.getBoundingClientRect();
    return box ? { x: box.left, y: box.bottom + 6 } : undefined;
  };

  const take = (substanceId: string): void => {
    onAdd(substanceId, below());
    // Cleared on use: the next thing typed is a new search, and a field still
    // holding the last reagent reads as if it is still in hand.
    setQuery('');
  };

  return (
    <div className="chemlab-find" ref={ref}>
      <input
        type="search"
        className="chemlab-find-input"
        placeholder="Find a reagent"
        aria-label="Find a reagent by name or formula"
        /*
         * Keep the BROWSER's suggestions out of it.
         *
         * A bare text field beside a page title is exactly what a browser
         * takes for a name or an address, so it opened its own saved-form menu
         * over these results — a student typing "a" was offered the people the
         * laptop's owner had last typed into a form. `autocomplete="off"` is
         * the ask; the rest is what makes it stick: a name nothing saved could
         * ever match, a role that says this field has a list of its own, and
         * the opt-outs the password managers read.
         */
        name="chemlab-find-reagent"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        role="combobox"
        aria-expanded={hits.length > 0}
        aria-autocomplete="list"
        data-1p-ignore
        data-lpignore="true"
        data-form-type="other"
        value={query}
        disabled={disabled}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setQuery('');
          // Enter is the keyboard's Add, on the first hit: the whole point of
          // typing a name you already know is not having to aim at a list
          // afterwards. The mouse still has to press the button.
          if (e.key === 'Enter' && hits[0]) take(hits[0].id);
        }}
      />
      {query.trim() && (
        <div className="chemlab-find-hits">
          {hits.length === 0
            ? <p className="chemlab-empty-hint">Nothing on the shelf by that name.</p>
            /*
             * The ROW is not the button; the button is. A list you are reading
             * to find something, where touching a line pours it into the
             * glassware, is a list you cannot read — and the reagent that goes
             * in is a decision, not a hover.
             */
            : hits.map(s => (
              <div key={s.id} className="chemlab-find-hit">
                <SubstanceIcon physicalState={s.physicalState} color={s.color} form={s.form} unit={s.dose?.unit} />
                <span className="chemlab-find-hit-text">
                  <span className="chemlab-find-hit-name">{s.displayName}</span>
                  {s.formula && (
                    <span className="chemlab-find-hit-formula"><ChemFormula formula={s.formula} fallback="" /></span>
                  )}
                </span>
                <button
                  className="chemlab-find-add"
                  title={`Add ${s.displayName} to the selected vessel`}
                  onClick={() => take(s.id)}
                >
                  Add
                </button>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
